import { AssistPanel } from '@adornis/chat-assist/client/AssistPanel.js';
import { useAssistContext } from '@adornis/chat-assist/client/assist-context.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html } from '@lit-labs/preact-signals';
import logo from '../../logos/sg-sankt-martinus.png';
import { Permission } from '../../permissions/roles.js';
import { askTeachingAssistant } from '../api/ask-teaching-assistant.js';

/** Offered on the empty screen; the assist sends them like any suggestion. */
const samplePrompts = [
  'Erstelle ein Mathe-Quiz für die 5. Klasse',
  'Formuliere Fragen zum Leseverständnis',
  'Entwirf ein Arbeitsblatt zu einem Experiment',
  'Gestalte Vokabelübungen',
  'Erstelle einen Zeitstrahl für Geschichte',
  'Schreib einen Impuls für kreatives Schreiben',
];

const welcome = html`
  <div class="space-y-6 py-4 text-center">
    <div class="space-y-3">
      <img class="mx-auto" ${css({ height: '96px' })} src=${logo} />
      <h1 class="text-3xl font-semibold text-base-content">🎓 Assistent</h1>
      <p class="mx-auto max-w-2xl text-base text-base-content/70">
        Dein kreativer KI-Begleiter für abwechslungsreiche Übungen. Gemeinsam machen wir den Unterricht lebendig und
        interaktiv. ✨
      </p>
      <!-- true as long as startup.ts points OpenRouter at its EU endpoint and runs no model router -->
      <span
        class="inline-flex items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-3 py-1 text-sm font-medium text-success"
      >
        <span class="icon-[material-symbols--verified-user-outline] text-sm"></span>
        KI-Verarbeitung ausschließlich in der EU
      </span>
    </div>
    <div class="mx-auto grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-3">
      <div class="card">
        <div class="card-body p-5 text-center">
          <div class="mb-2 text-3xl text-primary">📝</div>
          <h2 class="mb-1 font-semibold md:text-lg text-base-content">Übungen erstellen</h2>
          <p class="text-sm text-base-content/70">Quizze, Arbeitsblätter und interaktive Aufgaben</p>
        </div>
      </div>
      <div class="card">
        <div class="card-body p-5 text-center">
          <div class="mb-2 text-3xl text-secondary">🎯</div>
          <h2 class="mb-1 font-semibold md:text-lg text-base-content">Differenzieren</h2>
          <p class="text-sm text-base-content/70">Aufgaben passend zu Klassenstufe und Lerntyp</p>
        </div>
      </div>
      <div class="card">
        <div class="card-body p-5 text-center">
          <div class="mb-2 text-3xl text-accent">🎨</div>
          <h2 class="mb-1 font-semibold md:text-lg text-base-content">Kreative Formate</h2>
          <p class="text-sm text-base-content/70">Spiele, Rätsel und Challenges für die Klasse</p>
        </div>
      </div>
    </div>
    <h2 class="font-semibold md:text-lg text-base-content/80">🚀 Womit legen wir los?</h2>
  </div>
`;

export const SGTeachingAssistant = stateful(() => {
  const user = useObservable(() => currentUser(), []);

  useAssistContext({ key: 'teaching-assistant', label: 'Assistent', suggestions: samplePrompts });

  if (!user?.hasPermission(Permission.VIEW_TEACHING_ASSISTANT)) return html` <div>Keine Berechtigung</div> `;

  return html`
    <div class="flex min-h-0 flex-grow flex-col bg-base-100">
      ${AssistPanel({
        ask: (conversationID, text) => askTeachingAssistant(conversationID, text)(),
        branding: {
          name: 'Assistent',
          // the sidebar and the welcome screen show the logo already
          icon: null,
          placeholder: 'Was brauchst du für deinen Unterricht?',
          welcome,
        },
      })}
    </div>
  `;
});
